// Hooks for the study surfaces (Reflect, Insights, the Bible map). Reflections are synced notes tagged
// "reflection" (src/sync/reflections.ts): kept on every device the reader signs in to, encrypted at rest
// on the server, and readable in the app. Reflections kept in localStorage before sync are moved into
// notes once (sync/react.tsx). Outside the signed-in shell (no sync engine) they fall back to the
// study source's own.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useMemo } from "react";
import { asNote, REFLECTION_TAG } from "../sync/domain";
import { useCollection, useSyncEngine } from "../sync/react";
import { noteToReflection, reflectionToNote } from "../sync/reflections";
import { createMockStudy, type Reflection, type StudySource } from "./study";

export const StudyContext = createContext<StudySource | null>(null);
const fallback = createMockStudy();
export const useStudy = () => useContext(StudyContext) ?? fallback;

export const studyKeys = {
  chapters: ["study", "chapters"] as const,
  reflections: ["study", "reflections"] as const,
};

export const useChaptersRead = () => {
  const s = useStudy();
  return useQuery({ queryKey: studyKeys.chapters, queryFn: () => s.chaptersRead() });
};

export const useReflections = () => {
  const s = useStudy();
  const engine = useSyncEngine();
  const notes = useCollection("note");
  const local = useQuery({ queryKey: studyKeys.reflections, queryFn: () => s.reflections(), enabled: !engine });
  const synced = useMemo(
    () =>
      (notes.data ?? [])
        .map(asNote)
        .filter((n) => n.tags.includes(REFLECTION_TAG) && n.deletedAt === null)
        .map(noteToReflection)
        .sort((a, b) => b.date.localeCompare(a.date)),
    [notes.data],
  );
  return engine ? { ...notes, data: synced } : local;
};

export const useSaveReflection = () => {
  const s = useStudy();
  const engine = useSyncEngine();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (r: Omit<Reflection, "id" | "date">): Promise<Reflection> => {
      if (!engine) return s.saveReflection(r);
      const change = reflectionToNote(r);
      await engine.mutate([change]);
      return { ...r, id: change.id, date: new Date().toISOString().slice(0, 10) };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: studyKeys.reflections }),
  });
};
